import { CircularProgress, LinearProgress } from '@mui/joy';

import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';

import { DateTimeRangePicker } from '@/components/DateTimeRangePicker/DateTimeRangePicker';
import { SingleFeedbackChipWithSourceRunAction } from '@/components/FeedbackChips';
import { Pane } from '@/components/Pane';
import {
  DateTimeRangePickerValue,
  parseRelativeDate,
} from '@/components/RunsTable/utils/getDateTimeRangeLabel';
import { Tooltip } from '@/design-system';
import {
  useOrganizationId,
  useRunRuleLogsInfinite,
  useRunRulesLastApplied,
} from '@/hooks/useSwr';
import AlertCircleIcon from '@/icons/AlertCircleIcon.svg?react';
import CheckCircleIcon from '@/icons/CheckCircleIcon.svg?react';
import SkipCircleIcon from '@/icons/SkipCircleIcon.svg?react';
import {
  FeedbackSchema,
  RuleLogActionSchema,
  RuleLogSchema,
  RuleSchema,
} from '@/types/schema';
import {
  appDatasetsPath,
  appExamplePath,
  appOrganizationPath,
  appProjectsPath,
  appRunPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';
import { localTime, utcTime } from '@/utils/utc-time';

import { RunType } from '../../Pages/Run/components/RunType';

dayjs.extend(minMax);
dayjs.extend(utc);
function RuleLogActionIcon(props: {
  isRoot?: boolean;
  action: RuleLogActionSchema | undefined;
}) {
  if (!props.isRoot && !props.action) return null;

  if (!props.action || props.action.outcome === 'skipped') {
    return (
      <Tooltip title="No runs matched, skipping">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-tertiary">
          <SkipCircleIcon className="h-5 w-5" />
        </div>
      </Tooltip>
    );
  }

  if (props.action.outcome === 'success') {
    return (
      <Tooltip title="Successful action">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-success-primary text-success">
          <CheckCircleIcon className="h-5 w-5" />
        </div>
      </Tooltip>
    );
  }

  const error = Array.isArray(props.action.payload?.errors)
    ? props.action.payload?.errors.join('\n')
    : props.action.payload?.error;

  const strError =
    error != null
      ? typeof error === 'string'
        ? error
        : JSON.stringify(error)
      : null;

  return (
    <Tooltip
      title={
        strError ? (
          <span className="block max-w-[320px] whitespace-pre-wrap">
            {strError}
          </span>
        ) : null
      }
    >
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-error-secondary text-error">
        <AlertCircleIcon className="h-5 w-5" />
      </div>
    </Tooltip>
  );
}

function getFirstAction(log: RuleLogSchema) {
  return [
    log.add_to_annotation_queue,
    log.add_to_dataset,
    log.evaluators,
    log.alerts,
    log.webhooks,
  ].find((action) => action);
}

function isEmptyLog(log: RuleLogSchema) {
  return (
    log.run_id === '00000000-0000-0000-0000-000000000000' &&
    getFirstAction(log) == null
  );
}

function getLogDateTimeLabel(value: { start_time: string; end_time: string }) {
  const start = localTime(value?.start_time);
  const end = localTime(value?.end_time);
  const now = dayjs();

  if (start.format('MM/DD/YYYY') === end.format('MM/DD/YYYY')) {
    if (start.format('MM/DD/YYYY') === now.format('MM/DD/YYYY')) {
      return `Today ${start.format('hh:mm:ss A')} - ${end.format(
        'hh:mm:ss A'
      )}`;
    }

    return `${start.format('MM/DD/YYYY hh:mm:ss A')} - ${end.format(
      'hh:mm:ss A'
    )}`;
  }

  return `${start.format('MM/DD/YYYY, hh:mm:ss A')} - ${end.format(
    'MM/DD/YYYY, hh:mm:ss A'
  )}`;
}

const isFeedbackSchema = (value: unknown): value is FeedbackSchema => {
  if (typeof value !== 'object' || value === null) return false;
  return 'key' in value && typeof value.key === 'string';
};

function RuleLogEvaluatorFeedback(props: { log: RuleLogSchema }) {
  if (!props.log.evaluators?.payload) return null;

  const feedbackIds = props.log.evaluators.payload['feedback_ids'];
  if (!feedbackIds || !Array.isArray(feedbackIds)) return null;

  const feedbackMetadata = props.log.evaluators.payload['feedback_metadata'];
  if (!feedbackMetadata || typeof feedbackMetadata !== 'object') return null;

  return (
    <div className="flex flex-shrink flex-wrap items-center gap-1.5">
      {feedbackIds.map((feedbackId, idx) => {
        const feedback = feedbackMetadata?.[feedbackId];
        if (!isFeedbackSchema(feedback)) return null;
        const renderAsCategorical = typeof feedback.value === 'string';

        return (
          <SingleFeedbackChipWithSourceRunAction
            key={idx}
            feedbackKey={feedback.key}
            avg={renderAsCategorical ? undefined : feedback.score}
            n={1}
            values={
              renderAsCategorical ? { [feedback.value ?? '']: 1 } : undefined
            }
            feedbackSourceRunId={props.log.run_id}
            allowTruncation={true}
            showNotes={true}
          />
        );
      })}
    </div>
  );
}

function RuleLogExampleAction(props: { log: RuleLogSchema }) {
  const organizationId = useOrganizationId();
  if (!props.log.add_to_dataset) return null;

  const payload = props.log.add_to_dataset.payload;
  if (!payload || !payload.example_id || !payload.dataset_id) return null;

  return (
    <Link
      to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${payload.dataset_id}/${appExamplePath}/${payload.example_id}`}
      className="rounded-md border border-secondary px-2 py-0.5 text-sm transition-colors hover:bg-secondary-hover active:bg-secondary"
      target="_blank"
    >
      View Example
    </Link>
  );
}

const PAGE_SIZE = 720;

function RuleLogsFooter(props: {
  context?: {
    hasMore?: boolean;
    onVisible?: () => void;
    onHidden?: () => void;
  };
}) {
  const ref = useRef<HTMLButtonElement>(null);

  const { onVisible, onHidden, hasMore } = props.context ?? {};

  useEffect(() => {
    if (ref.current) {
      let lastVal = false;
      const observer = new IntersectionObserver(
        ([entry]) => {
          const nextVal = entry.isIntersecting;
          if (nextVal !== lastVal) {
            (nextVal ? onVisible : onHidden)?.();
          }
          lastVal = nextVal;
        },
        { root: null, threshold: 0.1 }
      );

      observer.observe(ref.current);
    }
  }, [onVisible, onHidden, hasMore]);

  if (!hasMore) return null;
  return (
    <span
      ref={ref}
      className={cn(
        'mx-auto flex items-center justify-center gap-2 rounded-lg p-2 px-4 pb-4 text-center text-tertiary'
      )}
    >
      <CircularProgress size="sm" color="neutral" sx={{ opacity: 0.5 }} />
      <span>Loading...</span>
    </span>
  );
}

function RuleLogs(props: { rule: RuleSchema }) {
  const organizationId = useOrganizationId();
  const [timeModel, setTimeModel] = useState<DateTimeRangePickerValue>({
    duration: props.rule.backfill_from ? undefined : '1d',
  });

  const timeFilter = useMemo(() => {
    let startDuration;
    let startTime = timeModel?.start_time;
    let endTime = timeModel?.end_time;

    if (timeModel?.duration) {
      startDuration = parseRelativeDate(timeModel.duration);
      if (startDuration) {
        startTime = dayjs
          .utc()
          .subtract(startDuration.value, startDuration.unit)
          .toISOString();
        endTime = undefined;
      }
    }

    return { start_time: startTime, end_time: endTime };
  }, [timeModel]);

  const logs = useRunRuleLogsInfinite(
    props.rule.id,
    timeFilter?.start_time,
    timeFilter?.end_time,
    PAGE_SIZE
  );

  const lastApplied = useRunRulesLastApplied(props.rule.id);

  const compressedLogs = useMemo(() => {
    return logs.data?.flat()?.reduce<RuleLogSchema[]>((memo, item) => {
      const lastItem = memo.at(-1);
      if (!lastItem) return [item];

      if (isEmptyLog(item) && isEmptyLog(lastItem)) {
        lastItem.start_time =
          dayjs
            .min(utcTime(item.start_time), utcTime(lastItem.start_time))
            ?.toISOString() ?? '';
        lastItem.end_time =
          dayjs
            .max(utcTime(item.end_time), utcTime(lastItem.end_time))
            ?.toISOString() ?? '';
        return memo;
      }

      return [...memo, item];
    }, []);
  }, [logs.data]);

  const lastTotal = logs.data?.at(-1)?.headers?.['x-pagination-total'];
  const expectedLength = logs.size * PAGE_SIZE;
  const hasMore = lastTotal != null ? lastTotal > expectedLength : undefined;
  const [keepLoading, setKeepLoading] = useState(false);

  useEffect(() => {
    if (hasMore && keepLoading) {
      logs.setSize((size) => size + 1);
    }
  }, [hasMore, keepLoading, logs]);

  const onVisible = useCallback(() => setKeepLoading(true), []);
  const onHidden = useCallback(() => setKeepLoading(false), []);

  return (
    <div className="flex flex-grow flex-col gap-4">
      {lastApplied.data && (
        <div className="flex flex-col gap-1">
          <div className="text-sm text-disabled">
            Last interval processed:{' '}
            {new Date(lastApplied.data?.start_time).toLocaleString()} to{' '}
            {new Date(lastApplied.data?.end_time).toLocaleString()}
          </div>
          <div className="text-sm text-disabled">
            Last time run:{' '}
            {new Date(lastApplied.data?.application_time).toLocaleString()}
          </div>
        </div>
      )}
      <div className="flex justify-between">
        <DateTimeRangePicker value={timeModel} onChange={setTimeModel} />
      </div>
      {logs.isLoading && (
        <div>
          <LinearProgress />
        </div>
      )}

      {compressedLogs?.length === 0 && !logs.isLoading && (
        <div className="text-tertiary">No logs found</div>
      )}

      {Array.isArray(compressedLogs) &&
        compressedLogs.length > 0 &&
        !logs.isLoading && (
          <Virtuoso
            data={compressedLogs}
            className="flex-grow overflow-hidden rounded-md border border-secondary"
            itemContent={(idx, log) => {
              const firstAction = getFirstAction(log);
              const commonCls = cn(
                'border-b border-secondary px-3 py-1.5 last-of-type:border-none'
              );

              if (log.run_id === '00000000-0000-0000-0000-000000000000') {
                return (
                  <div
                    key={idx}
                    className={cn(
                      commonCls,
                      'flex items-center gap-2 bg-secondary text-tertiary'
                    )}
                  >
                    <RuleLogActionIcon
                      isRoot
                      action={firstAction ?? undefined}
                    />

                    {getLogDateTimeLabel(log)}
                  </div>
                );
              }

              return (
                <div
                  key={idx}
                  className={cn(
                    commonCls,
                    'grid max-w-full grid-cols-[1fr,auto] items-center border-b border-secondary pl-11'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {log.run_name && log.run_type ? (
                      <>
                        <RuleLogActionIcon action={firstAction ?? undefined} />
                        <RunType
                          run={{ name: log.run_name, run_type: log.run_type }}
                        />
                        <span>{log.run_name}</span>
                      </>
                    ) : (
                      <span className="text-sm">{log.run_id}</span>
                    )}

                    <RuleLogEvaluatorFeedback log={log} />
                  </div>

                  <div className="flex items-center gap-1">
                    <RuleLogExampleAction log={log} />

                    <Link
                      to={`/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${log.run_session_id}/${appRunPath}/${log.run_id}`}
                      className="rounded-md border border-secondary px-2 py-0.5 text-sm transition-colors hover:bg-secondary-hover active:bg-secondary"
                      target="_blank"
                    >
                      View Run
                    </Link>
                  </div>
                </div>
              );
            }}
            context={{
              hasMore: hasMore || logs.isValidating,
              onVisible,
              onHidden,
            }}
            components={{ Footer: RuleLogsFooter }}
          />
        )}
    </div>
  );
}

export function RuleLogsPane(props: {
  open: boolean;
  onClose: () => void;
  rule?: RuleSchema;
}) {
  return (
    <Pane
      title={`Automation Logs for ${
        props.rule?.display_name || props.rule?.id
      } (1D)`}
      open={props.open}
      onClose={props.onClose}
      dialogStyle={{
        marginLeft: 'calc(max(4.5rem, 100vw - max(20vw, 800px)))',
      }}
      className="flex flex-col"
    >
      {props.rule && <RuleLogs rule={props.rule} />}
    </Pane>
  );
}
